import UtilityBar from '../components/layout/UtilityBar';
import Nav from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import LincolnAvatar from '../components/ui/LincolnAvatar';
import ImageSlot from '../components/ui/ImageSlot';
import saleemPhoto from '../assets/images/saleem.png';
import aishaPhoto from '../assets/images/aisha saleem.png';
import samiPhoto from '../assets/images/sami kazi.png';
import cajPhoto from '../assets/images/Caj Södergård.png';
import shainPhoto from '../assets/images/Shain Hymon.jpg';

const FOUNDERS = [
  { name: 'Saleem Qureshi', role: 'Co-founder & CEO', bio: '20+ years in education tech. Built Lincoln Method (now Lincoln School) from Karachi to Helsinki.',
    photo: saleemPhoto },
  { name: 'Aisha Saleem',   role: 'Co-founder & COO', bio: "Co-architect of Lincoln's voice curriculum. Ed-tech operator who scaled to 10 countries.",
    photo: aishaPhoto },
];

const ADVISORS = [
  { name: 'Sami Kazi',     role: 'Board Advisor', bio: 'VTT Senior Scientist. AI research and applied learning.', photo: samiPhoto },
  { name: 'Caj Södergård', role: 'Board Advisor', bio: '30+ years of Finnish AI research. Author, Aalto University.', photo: cajPhoto },
];

const TEAM = [
  { name: 'Shain Hymon', role: 'Team Member', photo: shainPhoto },
];

const TIMELINE = [
  { y: '2008', t: 'Lincoln Method founded',          d: 'Saleem launches a small tutoring agency in Karachi. The Lincoln name comes from a love of teaching like Lincoln Douglas — Socratic, voice-first.' },
  { y: '2015', t: 'Personalized tutoring at scale',  d: 'Lincoln Method tutors 4,000+ students globally. We discover that 1:1 voice tutoring is the highest-impact intervention.' },
  { y: '2022', t: 'Voice AI breakthrough',           d: 'We see GPT-3.5 and realize a voice tutor is now technically possible. We rebuild from scratch with voice as the front door.' },
  { y: '2024', t: 'Lincoln launches in Malta',       d: 'Partner with the Ministry of Education of Malta. First national rollout — all 10 secondary schools in 12 months.' },
  { y: '2025', t: 'WEF partnership',                  d: "Lincoln becomes the World Economic Forum's voice-AI literacy partner. AI Literacy 101 becomes free globally." },
  { y: '2026', t: 'Lincoln School',                   d: "We're not a tutoring company anymore — we're a school. Available in 3 languages, 10 countries, 80+ providers." },
];

export default function About() {
  return (
    <div style={{ background: 'var(--bg)' }}>
      <UtilityBar />
      <Nav variant="light" />

      {/* Hero */}
      <section className="relative overflow-hidden text-white" style={{ background: 'var(--navy)', padding: '100px 0' }}>
        <div className="absolute inset-0"
          style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="lc-container on-navy relative" style={{ zIndex: 1 }}>
          <div className="grid items-center" style={{ gridTemplateColumns: '1.4fr 1fr', gap: 60 }}>
            <div>
              <span className="lc-eyebrow on-dark">About Lincoln School</span>
              <h1 className="text-white mt-4" style={{ fontSize: 76, letterSpacing: '-0.03em', lineHeight: 1 }}>
                We&apos;re building a <em className="italic" style={{ color: 'var(--yellow)' }}>school</em><br />
                you can <em className="italic" style={{ color: 'var(--yellow)' }}>talk to</em>.
              </h1>
              <p className="mt-6 leading-relaxed" style={{ fontSize: 20, color: 'rgba(255,255,255,0.78)', maxWidth: 580 }}>
                Most AI education tools think of voice as a feature. We think of it as the front door. Lincoln is a school you can talk to. — built in Helsinki, serving 10 countries.
              </p>
            </div>
            <div className="relative flex justify-center">
              <ImageSlot
                id="about-hero"
                style={{ width: 440, height: 360 }}
                shape="rounded"
                radius="24"
                src="https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=1200&auto=format&fit=crop&q=80"
                placeholder="Drop a hero photo — team, classroom, students with Lincoln"
              />
              <div className="absolute" style={{ bottom: -28, right: -20 }}>
                <LincolnAvatar size={120} pulse />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="lc-section">
        <div className="lc-container" style={{ maxWidth: 920 }}>
          <div className="grid" style={{ gridTemplateColumns: '200px 1fr', gap: 56 }}>
            <div><span className="lc-eyebrow">Our mission</span></div>
            <div>
              <h2 style={{ fontSize: 44, letterSpacing: '-0.02em' }}>
                A world-class tutor in every pocket, in every language.
              </h2>
              <p className="mt-5.5 leading-loose" style={{ marginTop: 22, fontSize: 17, color: 'var(--ink-2)' }}>
                There are 2 billion students on Earth. There are not 2 billion great tutors. That gap — between the kids who get one-on-one attention and the kids who don&apos;t — is the biggest unfairness in modern education.
              </p>
              <p className="mt-4 leading-loose" style={{ fontSize: 17, color: 'var(--ink-2)' }}>
                Voice AI changes that. A tutor that listens, explains, quizzes, and certifies — in any language, at any hour, free for anyone — is no longer science fiction. It&apos;s Lincoln. And we&apos;re rolling him out, country by country, school by school.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="lc-section" style={{ background: 'var(--bg-cream)' }}>
        <div className="lc-container">
          <div className="text-center mb-12">
            <span className="lc-eyebrow">What we believe</span>
            <h2 className="mt-3.5">Five things that aren&apos;t up for debate.</h2>
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
            {[
              { icon: '🎙', title: 'Voice is the front door', desc: 'Reading and typing are barriers. Talking is universal.' },
              { icon: '🌐', title: 'In every language',       desc: "Learning in your mother tongue isn't a feature — it's a right." },
              { icon: '🆓', title: 'Free for learners',       desc: "Money should never be the reason a student can't learn." },
              { icon: '📐', title: 'Grounded, not hallucinating', desc: "Lincoln cites your material. He doesn't make things up." },
              { icon: '🤝', title: 'Teachers, not replaced',   desc: "Lincoln frees teachers from grading. Their job gets harder, not easier — and that's the point." },
            ].map((v) => (
              <div key={v.title} className="bg-white" style={{ borderRadius: 16, padding: 24, border: '1px solid var(--line)' }}>
                <div className="flex items-center justify-center mb-4"
                  style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--yellow)', fontSize: 24 }}>{v.icon}</div>
                <h4 style={{ fontSize: 16 }}>{v.title}</h4>
                <p className="mt-2 leading-relaxed" style={{ fontSize: 13, color: 'var(--muted)' }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="lc-section">
        <div className="lc-container" style={{ maxWidth: 920 }}>
          <div className="text-center mb-12">
            <span className="lc-eyebrow">Our story</span>
            <h2 className="mt-3.5">From Karachi to Helsinki, in 18 years.</h2>
          </div>
          <div className="relative pl-10">
            <div className="absolute" style={{ left: 11, top: 8, bottom: 8, width: 2, background: 'var(--line)' }} />
            {TIMELINE.map((m) => (
              <div key={m.y} className="relative" style={{ paddingBottom: 32 }}>
                <div className="absolute rounded-full bg-white"
                  style={{ left: -35, top: 6, width: 18, height: 18, border: '3px solid var(--yellow)' }} />
                <div className="font-bold tracking-wider" style={{ fontSize: 13, color: 'var(--blue)' }}>{m.y}</div>
                <div className="font-serif font-semibold mt-1" style={{ fontSize: 22 }}>{m.t}</div>
                <p className="mt-1.5 leading-loose" style={{ fontSize: 15, color: 'var(--muted)' }}>{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founders + Team */}
      <section className="lc-section" style={{ background: 'var(--bg-cream)' }}>
        <div className="lc-container">
          <div className="text-center mb-12">
            <span className="lc-eyebrow">Founders</span>
            <h2 className="mt-3.5">The people behind Lincoln.</h2>
          </div>

          <div className="grid mb-16" style={{ gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {FOUNDERS.map((f) => (
              <div key={f.name} className="bg-white flex overflow-hidden" style={{ borderRadius: 20, border: '1px solid var(--line)' }}>
                <ImageSlot
                  id={`founder-${f.name.replace(/\s/g, '-')}`}
                  style={{ width: 200, height: 240, flexShrink: 0 }}
                  shape="rect"
                  src={f.photo}
                  placeholder={`Photo: ${f.name}`}
                />
                <div className="flex flex-col justify-center" style={{ padding: 24 }}>
                  <h3 style={{ fontSize: 24 }}>{f.name}</h3>
                  <div className="font-bold mt-1 mb-3" style={{ fontSize: 13, color: 'var(--blue)' }}>{f.role.toUpperCase()}</div>
                  <p className="leading-relaxed" style={{ fontSize: 14, color: 'var(--muted)' }}>{f.bio}</p>
                  <div className="flex gap-2 mt-4">
                    {['in', '𝕏', '✉'].map((s) => (
                      <button key={s} type="button" className="rounded-full font-bold"
                        style={{ width: 30, height: 30, background: 'var(--bg-soft)', color: 'var(--ink)', fontSize: 12 }}>{s}</button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Advisors */}
          <div className="mb-14">
            <h3 className="mb-5" style={{ fontSize: 24 }}>Board &amp; Advisors</h3>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
              {ADVISORS.map((a) => (
                <div key={a.name} className="bg-white" style={{ borderRadius: 14, padding: 20, border: '1px solid var(--line)' }}>
                  <ImageSlot id={`adv-${a.name.replace(/\s/g, '-')}`} style={{ width: 64, height: 64, marginBottom: 14 }} shape="circle" src={a.photo} placeholder="" />
                  <div className="font-bold" style={{ fontSize: 15 }}>{a.name}</div>
                  <div className="font-bold mt-1 tracking-wider" style={{ fontSize: 11, color: 'var(--blue)' }}>{a.role.toUpperCase()}</div>
                  <p className="mt-2 leading-relaxed" style={{ fontSize: 12, color: 'var(--muted)' }}>{a.bio}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Team */}
          <div>
            <h3 className="mb-5" style={{ fontSize: 24 }}>The team</h3>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
              {TEAM.map((t) => (
                <div key={t.name} className="flex items-center gap-3 bg-white"
                  style={{ padding: 12, borderRadius: 12, border: '1px solid var(--line)' }}>
                  <ImageSlot id={`team-${t.name.replace(/\s/g, '-')}`} style={{ width: 44, height: 44, flexShrink: 0 }} shape="circle" src={t.photo} placeholder="" />
                  <div className="min-w-0">
                    <div className="font-bold" style={{ fontSize: 14, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{t.role}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <a href="#" className="lc-btn ghost">We&apos;re hiring — see open roles →</a>
            </div>
          </div>
        </div>
      </section>

      {/* Press */}
      <section className="lc-section on-navy" style={{ background: 'var(--navy)', padding: '80px 0' }}>
        <div className="lc-container">
          <div className="text-center mb-9">
            <span className="lc-eyebrow on-dark">In the press</span>
            <h2 className="text-white mt-3.5">What people are saying about Lincoln.</h2>
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {[
              { o: 'TechCrunch',         q: "The first voice-AI company that's actually deployed in real classrooms — at national scale." },
              { o: 'EU Education Brief', q: "Malta's voice-first AI program with Lincoln is the EU's blueprint for the next decade." },
              { o: 'Helsingin Sanomat',  q: "Finland's quiet ed-tech export — Lincoln is teaching the world while keeping headquarters in Helsinki." },
            ].map((p) => (
              <div key={p.o} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', padding: 24, borderRadius: 16 }}>
                <div className="font-bold tracking-widest mb-3" style={{ fontSize: 11, color: 'var(--yellow-2)' }}>{p.o.toUpperCase()}</div>
                <p className="font-serif italic leading-tight text-white" style={{ fontSize: 16 }}>&ldquo;{p.q}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
